import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import Tooltip from '../components/Tooltip';

function Campaigns({ practiceId }) {
  const [enrollments, setEnrollments] = useState([]);
  const [campaignTypes, setCampaignTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const defaultStartDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  };
  const [filter, setFilter] = useState({ campaignType: '', status: '', startDate: defaultStartDate(), endDate: new Date().toISOString().split('T')[0] });
  const [searchParams, setSearchParams] = useSearchParams();
  const [setupBannerDismissed, setSetupBannerDismissed] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const [running, setRunning] = useState(null);
  const [aiOptimizations, setAiOptimizations] = useState([]);
  const [applyingOpt, setApplyingOpt] = useState(null);

  useEffect(() => {
    if (practiceId) {
      loadCampaignTypes();
      loadAiOptimizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  useEffect(() => {
    if (practiceId) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, practiceId]);

  const loadAiOptimizations = async () => {
    try {
      const result = await api.getCampaignOptimizations(practiceId);
      if (result.success && result.data) {
        setAiOptimizations(
          (Array.isArray(result.data) ? result.data : []).filter(o => o.status === 'recommended')
        );
      }
    } catch (err) {
      // Silently fail — optional feature
    }
  };

  const handleApplyOptimization = async (optId) => {
    try {
      setApplyingOpt(optId);
      await api.applyCampaignOptimization(practiceId, optId);
      await loadAiOptimizations();
      alert('Optimization applied successfully.');
    } catch (err) {
      alert(`Failed to apply: ${err.message}`);
    } finally {
      setApplyingOpt(null);
    }
  };

  const loadCampaignTypes = async () => {
    try {
      const response = await api.getCampaignTypes(practiceId);
      setCampaignTypes(response.data || []);
    } catch (err) {
      console.error('Error loading campaign types:', err);
      // If no campaign types exist, initialize them
      if (err.message?.includes('404') || (err.message?.includes('Failed') && practiceId)) {
        try {
          await api.initializeCampaignTypes(practiceId);
          const response = await api.getCampaignTypes(practiceId);
          setCampaignTypes(response.data || []);
        } catch (initErr) {
          console.error('Error initializing campaign types:', initErr);
        }
      }
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const enrollmentsRes = await api.getEnrollments(filter.campaignType || null, filter.status || null, practiceId, filter.startDate || null, filter.endDate || null);
      setEnrollments(enrollmentsRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRunCampaign = async (campaignType) => {
    if (running) return;

    // Find the campaign type by slug
    const type = campaignTypes.find(t => t.slug === campaignType);

    try {
      setRunning(campaignType);

      // Use the new dynamic campaign endpoint if we have a type ID
      if (type?.id) {
        const result = await api.runCampaignType(type.id);
        await loadData();
        alert(`${type.name} campaign completed!\nEnrolled: ${result.data.enrolled}\nContacted: ${result.data.contacted}`);
      } else {
        // Fallback to old endpoint for backward compatibility
        await api.runCampaign(campaignType, practiceId);
        await loadData();
        alert(`${campaignType} campaign executed successfully!`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setRunning(null);
    }
  };

  const handleRunAllCampaigns = async () => {
    if (running) return;

    try {
      setRunning('all');
      const result = await api.runAllDynamicCampaigns(practiceId);
      await loadData();

      // Build summary message
      const summary = Object.entries(result.data)
        .map(([name, r]) => `${name}: ${r.enrolled} enrolled, ${r.contacted} contacted`)
        .join('\n');

      alert(`All campaigns completed!\n\n${summary}`);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setRunning(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'info',
      completed: 'success',
      exited: 'warning',
      unsubscribed: 'danger',
    };
    return badges[status] || 'info';
  };

  const getCampaignDisplayName = (slug) => {
    const type = campaignTypes.find(t => t.slug === slug);
    return type?.name || slug;
  };

  const handleQuickSetup = async () => {
    try {
      setInitializing(true);
      await api.initializeCampaignTypes(practiceId);
      await api.createDefaultTemplates(practiceId);
      await loadCampaignTypes();
      setSearchParams({});
      setSetupBannerDismissed(true);
    } catch (err) {
      alert(`Setup failed: ${err.message}`);
    } finally {
      setInitializing(false);
    }
  };

  const showSetupBanner = searchParams.get('setup') === 'true'
    && campaignTypes.length === 0
    && !setupBannerDismissed
    && !loading;

  if (loading) {
    return <div className="loading">Loading campaigns...</div>;
  }

  // Active campaign types for the run buttons
  const activeCampaignTypes = campaignTypes.filter(t => t.is_active);

  return (
    <div className="campaigns">
      {/* Sub-navigation tabs */}
      <div style={styles.subNav}>
        <Link to="/practice-view/campaigns" style={{ ...styles.subNavTab, ...styles.subNavTabActive }}>
          Campaigns
        </Link>
        <Link to="/practice-view/campaign-types" style={styles.subNavTab}>
          Campaign Settings
        </Link>
      </div>

      {showSetupBanner && (
        <div style={setupBannerStyles.banner}>
          <div style={setupBannerStyles.iconWrap}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={setupBannerStyles.title}>Set Up Campaigns</h3>
            <p style={setupBannerStyles.desc}>
              Import default campaign types (recall, reactivation, birthday, etc.) and message templates to get started quickly.
            </p>
          </div>
          <div style={setupBannerStyles.actions}>
            <button style={setupBannerStyles.primaryBtn} onClick={handleQuickSetup} disabled={initializing}>
              {initializing ? 'Setting Up...' : 'Import Defaults'}
            </button>
            <button style={setupBannerStyles.dismissBtn} onClick={() => { setSetupBannerDismissed(true); setSearchParams({}); }}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <h2>Campaign Management</h2>

        <div className="campaign-controls" style={styles.controls}>
          <button
            className="button"
            onClick={handleRunAllCampaigns}
            disabled={running === 'all'}
            style={styles.primaryButton}
          >
            {running === 'all' ? 'Running All...' : 'Run All Campaigns'}
          </button>

          <div style={styles.separator} />

          {activeCampaignTypes.map((type) => (
            <button
              key={type.id}
              className="button secondary"
              onClick={() => handleRunCampaign(type.slug)}
              disabled={running === type.slug}
              title={type.description}
            >
              {running === type.slug ? 'Running...' : `Run ${type.name}`}
            </button>
          ))}

          {activeCampaignTypes.length === 0 && (
            <span style={styles.noCampaigns}>
              No active campaign types. Configure them in Campaign Settings.
            </span>
          )}
        </div>
      </div>

      {/* AI Optimization Recommendations */}
      {aiOptimizations.length > 0 && (
        <div style={styles.aiBanner}>
          <div style={styles.aiBannerHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={styles.aiIcon}>&#9733;</span>
              <span style={styles.aiBannerTitle}>AI Optimization Recommendations</span>
              <span style={styles.aiBadge}>{aiOptimizations.length}</span>
            </div>
          </div>
          <div style={styles.aiOptList}>
            {aiOptimizations.map(opt => (
              <div key={opt.id} style={styles.aiOptCard}>
                <div style={{ flex: 1 }}>
                  <div style={styles.aiOptType}>
                    {opt.optimization_type?.replace(/_/g, ' ') || opt.campaign_type || 'General'}
                  </div>
                  <div style={styles.aiOptDesc}>
                    {opt.recommended_config
                      ? (typeof opt.recommended_config === 'string'
                          ? (() => { try { const c = JSON.parse(opt.recommended_config); return c.description || c.recommendation || JSON.stringify(c); } catch { return opt.recommended_config; } })()
                          : opt.recommended_config.description || opt.recommended_config.recommendation || JSON.stringify(opt.recommended_config))
                      : 'Optimization available'}
                  </div>
                  {opt.expected_improvement_pct != null && (
                    <span style={styles.aiImprovBadge}>
                      +{opt.expected_improvement_pct}% expected improvement
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button
                    onClick={() => handleApplyOptimization(opt.id)}
                    disabled={applyingOpt === opt.id}
                    style={styles.aiApplyBtn}
                  >
                    {applyingOpt === opt.id ? 'Applying...' : 'Apply'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <h2>Active Enrollments</h2>

        <div className="filters" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
          <select
            value={filter.campaignType}
            onChange={(e) => setFilter({ ...filter, campaignType: e.target.value })}
          >
            <option value="">All Campaigns</option>
            {campaignTypes.map((type) => (
              <option key={type.id} value={type.slug}>
                {type.name}
              </option>
            ))}
          </select>

          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="exited">Exited</option>
            <option value="unsubscribed">Unsubscribed</option>
          </select>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label style={{ fontSize: '13px', color: '#6c757d', whiteSpace: 'nowrap' }}>From:</label>
            <input
              type="date"
              value={filter.startDate}
              onChange={(e) => setFilter({ ...filter, startDate: e.target.value })}
              style={styles.dateInput}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label style={{ fontSize: '13px', color: '#6c757d', whiteSpace: 'nowrap' }}>To:</label>
            <input
              type="date"
              value={filter.endDate}
              onChange={(e) => setFilter({ ...filter, endDate: e.target.value })}
              style={styles.dateInput}
            />
          </div>

          {(filter.startDate || filter.endDate) && (
            <button
              onClick={() => setFilter({ ...filter, startDate: '', endDate: '' })}
              style={{ fontSize: '12px', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Clear dates (show all)
            </button>
          )}
        </div>

        <table className="table">
          <thead>
            <tr>
              <th>Patient</th>
              <th>Campaign</th>
              <th>Status <Tooltip text="Active, Completed, Exited, or Unsubscribed" /></th>
              <th>Enrolled Date <Tooltip text="When the patient was enrolled in this campaign" /></th>
              <th>Retry Count <Tooltip text="Number of contact attempts made for this patient" /></th>
              <th>Est. Revenue <Tooltip text="Estimated revenue if patient completes the campaign" /></th>
              <th>Recovered <Tooltip text="Actual revenue recovered from this enrollment" /></th>
            </tr>
          </thead>
          <tbody>
            {enrollments.length > 0 ? (
              enrollments.map((enrollment) => (
                <tr key={enrollment.id}>
                  <td>
                    {enrollment.first_name} {enrollment.last_name}
                  </td>
                  <td>
                    {getCampaignDisplayName(enrollment.campaign_type)}
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadge(enrollment.status)}`}>
                      {enrollment.status}
                    </span>
                  </td>
                  <td>{new Date(enrollment.enrolled_at).toLocaleDateString()}</td>
                  <td>{enrollment.retry_count}</td>
                  <td>${parseFloat(enrollment.estimated_revenue || 0).toFixed(2)}</td>
                  <td>${parseFloat(enrollment.recovered_revenue || 0).toFixed(2)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                  No enrollments found. Run campaigns to enroll patients.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  subNav: {
    display: 'flex',
    gap: '4px',
    marginBottom: '20px',
    borderBottom: '2px solid #e9ecef',
    paddingBottom: '0',
  },
  subNavTab: {
    padding: '12px 20px',
    textDecoration: 'none',
    color: '#6c757d',
    fontWeight: '500',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    transition: 'all 0.2s ease',
  },
  subNavTabActive: {
    color: '#0d6efd',
    borderBottomColor: '#0d6efd',
  },
  controls: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    alignItems: 'center',
  },
  primaryButton: {
    background: '#28a745',
  },
  separator: {
    width: '1px',
    height: '32px',
    background: '#ddd',
    margin: '0 8px',
  },
  noCampaigns: {
    color: '#666',
    fontStyle: 'italic',
  },
  dateInput: {
    padding: '6px 10px',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '13px',
    color: '#495057',
  },
  aiBanner: {
    background: 'linear-gradient(135deg, #f8f7ff, #f0f0ff)',
    border: '1px solid #c7d2fe',
    borderRadius: '8px',
    padding: '16px 20px',
    marginBottom: '20px',
  },
  aiBannerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  aiIcon: {
    fontSize: '16px',
    color: '#f59e0b',
  },
  aiBannerTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#4338ca',
  },
  aiBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    borderRadius: '10px',
    background: '#667eea',
    color: '#fff',
    fontSize: '11px',
    fontWeight: '600',
    padding: '0 6px',
  },
  aiOptList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  aiOptCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 14px',
    background: '#fff',
    borderRadius: '6px',
    border: '1px solid #e0e0f0',
  },
  aiOptType: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#1f2937',
    textTransform: 'capitalize',
    marginBottom: '2px',
  },
  aiOptDesc: {
    fontSize: '12px',
    color: '#6b7280',
    lineHeight: '1.4',
  },
  aiImprovBadge: {
    display: 'inline-block',
    marginTop: '4px',
    padding: '2px 8px',
    background: '#ecfdf5',
    color: '#059669',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '500',
  },
  aiApplyBtn: {
    padding: '6px 14px',
    background: '#667eea',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
};

const setupBannerStyles = {
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '16px 20px',
    marginBottom: 20,
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: 8,
  },
  iconWrap: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: '#dbeafe',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  title: { margin: 0, fontSize: 15, fontWeight: 600, color: '#1e40af' },
  desc: { margin: '4px 0 0', fontSize: 13, color: '#3b82f6', lineHeight: 1.4 },
  actions: { display: 'flex', gap: 8, flexShrink: 0 },
  primaryBtn: {
    padding: '8px 18px',
    backgroundColor: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  dismissBtn: {
    padding: '8px 14px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: 6,
    fontSize: 13,
    cursor: 'pointer',
  },
};

export default Campaigns;
